import { useState, useEffect } from 'react';
import { FieldRow } from '../../shared';
import type { MediaData } from './MediaSection';
import type { URLValidatorResult, URLValidatorRecommendation } from '../../../../types/url-validation';
import styles from './PreparationSection.module.css';

export interface PreparationSectionProps {
  data: MediaData;
  editable: boolean;
  validation: URLValidatorResult | null;
  validating: boolean;
  urlsDirty: boolean;
  onChange?: (updates: Partial<MediaData>) => void;
  onSaveOverrides?: (overrides: Partial<URLValidatorResult['data_availability']>) => void;
}

/**
 * PreparationSection - URL fields editor + validation display
 *
 * Combines Media table editing with URL validation results
 */
export function PreparationSection({
  data,
  editable,
  validation,
  validating,
  urlsDirty,
  onChange,
  onSaveOverrides
}: PreparationSectionProps) {
  const [localData, setLocalData] = useState<MediaData>(data);
  const [editMode, setEditMode] = useState(false);
  const [fieldOverrides, setFieldOverrides] = useState<Record<string, 'found' | 'missing'>>({});

  useEffect(() => {
    setLocalData(data);
  }, [data]);

  const recommendation: URLValidatorRecommendation = validation?.recommendation ?? 'not_run';

  // Merge validation data with manual overrides
  const effectiveFields = validation?.data_availability?.must_have_fields
    ? { ...validation.data_availability.must_have_fields, ...fieldOverrides }
    : null;

  const handleFieldChange = (field: keyof MediaData, value: string) => {
    const updates = { ...localData, [field]: value };
    setLocalData(updates);
    onChange?.(updates);
  };

  const getStatusBadge = () => {
    if (urlsDirty) {
      return <span className={`${styles.badge} ${styles.badgeWarning}`}>⚠️ URLs Modified - Re-validate Recommended</span>;
    }

    switch (recommendation) {
      case 'AUTO_CONTINUE':
      case 'approved_by_user':
        return <span className={`${styles.badge} ${styles.badgeSuccess}`}>✅ Ready for Data Extraction</span>;
      case 'REVIEW_URLS':
        return <span className={`${styles.badge} ${styles.badgeWarning}`}>⚠️ URLs Need Review</span>;
      case 'INSUFFICIENT_DATA':
        return <span className={`${styles.badge} ${styles.badgeError}`}>❌ Insufficient Data</span>;
      case 'MARK_INELIGIBLE':
        return <span className={`${styles.badge} ${styles.badgeError}`}>❌ Program Ineligible</span>;
      case 'ERROR':
        return <span className={`${styles.badge} ${styles.badgeError}`}>❌ Validation Error</span>;
      case 'not_run':
      default:
        return null; // Don't show badge when not yet validated
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        {getStatusBadge()}
      </div>

      {/* Editable Media Fields */}
      <div className={styles.fieldsContainer}>
        {validating && (
          <div className={styles.validationStatus}>Running BAM_check…</div>
        )}

        <FieldRow
          label="Website URL"
          value={localData.website_url}
          editable={editable}
          onChange={(v) => handleFieldChange('website_url', v)}
        />
        <FieldRow
          label="Submission URL"
          value={localData.submission_url}
          editable={editable}
          onChange={(v) => handleFieldChange('submission_url', v)}
        />
        <FieldRow
          label="Past Winners URL"
          value={localData.past_winners_url}
          editable={editable}
          onChange={(v) => handleFieldChange('past_winners_url', v)}
        />
        <FieldRow
          label="Logo URL"
          value={localData.logo_url}
          editable={editable}
          onChange={(v) => handleFieldChange('logo_url', v)}
        />
        <FieldRow
          label="Website Screenshot URL"
          value={localData.website_screenshot_url}
          editable={editable}
          onChange={(v) => handleFieldChange('website_screenshot_url', v)}
        />
        <FieldRow
          label="Seed URLs"
          value={localData.seed_urls}
          editable={editable}
          onChange={(v) => handleFieldChange('seed_urls', v)}
          multiline
          placeholder="Enter comma-separated URLs..."
        />
        <FieldRow
          label="Wikipedia Entry"
          value={localData.wikipedia_entry}
          editable={editable}
          onChange={(v) => handleFieldChange('wikipedia_entry', v)}
        />

        {/* Specialized URLs (discovered by URL-Validator agent) */}
        <FieldRow
          label="Categories URL"
          value={localData.categories_url}
          editable={editable}
          onChange={(v) => handleFieldChange('categories_url', v)}
        />
        <FieldRow
          label="Deadlines URL"
          value={localData.deadlines_url}
          editable={editable}
          onChange={(v) => handleFieldChange('deadlines_url', v)}
        />
        <FieldRow
          label="Fees URL"
          value={localData.fees_url}
          editable={editable}
          onChange={(v) => handleFieldChange('fees_url', v)}
        />
        <FieldRow
          label="Requirements URL"
          value={localData.requirements_url}
          editable={editable}
          onChange={(v) => handleFieldChange('requirements_url', v)}
        />
        <FieldRow
          label="Contact URL"
          value={localData.contact_url}
          editable={editable}
          onChange={(v) => handleFieldChange('contact_url', v)}
        />
        <FieldRow
          label="Guidelines URL"
          value={localData.guidelines_url}
          editable={editable}
          onChange={(v) => handleFieldChange('guidelines_url', v)}
        />
      </div>

      {/* Validation Results (if validation has run) */}
      {validation && !urlsDirty && (
        <div className={styles.validationResults}>
          {/* Header with Edit Toggle */}
          <div className={styles.resultsHeader}>
            <h5 className={styles.resultsTitle}>Validation Results</h5>
            <button
              type="button"
              className={styles.editToggle}
              onClick={() => setEditMode(!editMode)}
            >
              {editMode ? '👁️ View' : '✏️ Override Results'}
            </button>
          </div>

          {/* View Mode: Summary */}
          {!editMode && (
            <>
              {/* Data Availability Score */}
              {validation.data_availability && (
                <div className={styles.resultRow}>
                  <span className={styles.resultLabel}>Data Availability:</span>
                  <span className={styles.resultValue}>
                    {Math.round(validation.data_availability.availability_score * 100)}%
                    ({Object.values(effectiveFields || {}).filter(v => v === 'found').length}/
                    {Object.keys(validation.data_availability.must_have_fields).length} fields)
                  </span>
                </div>
              )}

              {/* Submission Type */}
              {validation.submission_type && (
                <div className={styles.resultRow}>
                  <span className={styles.resultLabel}>Submission Type:</span>
                  <span className={`${styles.resultValue} ${validation.submission_type.eligibility === 'ELIGIBLE' ? styles.textSuccess : styles.textError}`}>
                    {validation.submission_type.type === 'open_submission' ? '✅ Open (Authors can submit)' : '❌ ' + validation.submission_type.type}
                  </span>
                </div>
              )}

              {/* Missing Critical Fields */}
              {validation.data_availability?.missing_critical && validation.data_availability.missing_critical.length > 0 && (
                <div className={styles.resultRow}>
                  <span className={styles.resultLabel}>Missing Critical:</span>
                  <span className={styles.resultValue}>
                    {validation.data_availability.missing_critical.join(', ')}
                  </span>
                </div>
              )}

              {/* Flags */}
              {validation.flags && validation.flags.length > 0 && (
                <div className={styles.flags}>
                  {validation.flags.map((flag, idx) => (
                    <div key={idx} className={`${styles.flag} ${styles[`flag${flag.severity.charAt(0).toUpperCase() + flag.severity.slice(1)}`]}`}>
                      <span className={styles.flagSeverity}>{flag.severity}:</span>
                      <span className={styles.flagMessage}>{flag.message}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Error Message */}
              {validation.error && (
                <div className={styles.errorBox}>
                  <strong>Error:</strong> {validation.error}
                </div>
              )}
            </>
          )}

          {/* Edit Mode: Field-by-field overrides */}
          {editMode && effectiveFields && (
            <div className={styles.fieldOverrides}>
              <p className={styles.overrideHint}>
                Manually override field statuses if the agent assessment is incorrect
              </p>
              {Object.entries(effectiveFields).map(([field, status]) => (
                <div key={field} className={styles.fieldOverrideRow}>
                  <label className={styles.fieldLabel}>
                    {field.replace(/_/g, ' ')}
                  </label>
                  <div className={styles.statusToggle}>
                    <button
                      type="button"
                      className={`${styles.statusBtn} ${status === 'found' ? styles.statusBtnActive : ''}`}
                      onClick={() => setFieldOverrides({ ...fieldOverrides, [field]: 'found' })}
                    >
                      ✓ Found
                    </button>
                    <button
                      type="button"
                      className={`${styles.statusBtn} ${status === 'missing' ? styles.statusBtnActive : ''}`}
                      onClick={() => setFieldOverrides({ ...fieldOverrides, [field]: 'missing' })}
                    >
                      ✗ Missing
                    </button>
                  </div>
                </div>
              ))}
              <div className={styles.overrideActions}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => {
                    setFieldOverrides({});
                    setEditMode(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={() => {
                    if (onSaveOverrides && validation?.data_availability) {
                      const newMustHaveFields = { ...validation.data_availability.must_have_fields, ...fieldOverrides };
                      const foundCount = Object.values(newMustHaveFields).filter(v => v === 'found').length;
                      const missingCritical = Object.entries(newMustHaveFields)
                        .filter(([_, v]) => v === 'missing')
                        .map(([k, _]) => k);

                      onSaveOverrides({
                        must_have_fields: newMustHaveFields,
                        availability_score: foundCount / Object.keys(newMustHaveFields).length,
                        missing_critical: missingCritical
                      });
                    }
                    setFieldOverrides({});
                    setEditMode(false);
                  }}
                  disabled={Object.keys(fieldOverrides).length === 0}
                >
                  Save Overrides
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
